/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState} from 'react'
import {Input} from 'antd'
import {mdiBell, mdiBellOffOutline, mdiClose, mdiPencilOutline, mdiPill, mdiPillMultiple, mdiRestore, mdiTrashCanOutline} from '@mdi/js'
import Icon from '@mdi/react'
import {Chip, IconButton, Snackbar, Tooltip, Typography} from '@mui/material'
import {FormattedMessage} from 'react-intl'
import CardCustom from 'src/components/elements/CardCustom/CardCustom'
import Loader from 'src/components/elements/Loader/Loader'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import WarningPatientEmptyData from 'src/components/elements/WarningEmptyCard/WarningPatientEmptyData'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import TabsCustom from 'src/components/layouts/TabsLayout/TabsLayout'
import LayoutContainer from 'src/components/layouts/LayoutContainer/LayoutContainer'
import SecondaryTherapyDialog from 'src/components/templates/SecondaryTherapyDialog/SecondaryTherapyDialog'
import SecondaryTherapyCardTabs from 'src/components/templates/TherapyDialogs/SecondaryTherapyCardTabs'
import TherapyCardTabs from 'src/components/templates/TherapyDialogs/TherapyCardTabs'
import {formatTherapyState} from 'src/components/templates/TherapyDialogs/therapyFormatters'
import ConfirmationDialog from 'src/components/templates/ConfirmationDialog/ConfirmationDialog'
import {PATHS, STATUS} from 'src/constants/constants'
import {
  canOnSecondaryTherapy,
  formatReminderStatus,
  isReminderActive,
  REMINDER_STATUSES,
  SECONDARY_THERAPY_LOCKED_TOOLTIP,
  resolveSecondaryTherapyDataSchema,
  secondaryTherapyErrorMessage
} from 'src/constants/secondaryTherapyFields'
import apiClient from 'src/services/apiClient'
import {useAppSelector} from 'src/hooks'

// Read-only indicator of whether the assumption reminder is going out for this therapy, pinned to
// the card header's far right — legacy twin of the ReminderBell in pages-new/therapies/TherapiesNew.tsx.
// Deliberately just the bell: it is not a control, the reminder is switched on or off from the
// therapy form. Shared between primary and secondary therapies — `reminderStatus` is the same
// field on both (see constants/secondaryTherapyFields.ts).
const ReminderBell = ({therapy}: {therapy: Record<string, any>}) => {
  const isActive = isReminderActive(therapy)
  const label = formatReminderStatus(therapy)

  return (
    <Tooltip title={label}>
      <span aria-label={label} style={{display: 'inline-flex', alignItems: 'center'}}>
        <Icon color={isActive ? undefined : '#8C8C8C'} path={isActive ? mdiBell : mdiBellOffOutline} size={0.7} />
      </span>
    </Tooltip>
  )
}

const Therapies = () => {
  const sidebarItems = useAppSelector(state => state.pspConfigReducer.data.sidebarItems)
  const showPrimaryTherapies = sidebarItems?.primaryTherapies?.show
  const showSecondaryTherapies = sidebarItems?.secondaryTherapies?.show
  // Same source the "Aggiungi farmaco" wizard uses — keeps the card view and the create/edit
  // form showing the same set of fields.
  const secondaryTherapiesDataSchema = useAppSelector(
    state => resolveSecondaryTherapyDataSchema(state.pspConfigReducer.data.therapyConfigurations?.secondaryTherapiesConfiguration)
  )

  const [therapies, setTherapies] = useState<Record<string, any>[]>([])
  const [statusTherapies, setStatusTherapies] = useState<any>(undefined)

  const [secondaryTherapies, setSecondaryTherapies] = useState<Record<string, any>[]>([])
  const [statusSecondaryTherapies, setStatusSecondaryTherapies] = useState<any>(undefined)
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [editingSecondaryTherapy, setEditingSecondaryTherapy] = useState<Record<string, any> | undefined>(undefined)
  const [deletingSecondaryTherapy, setDeletingSecondaryTherapy] = useState<Record<string, any> | undefined>(undefined)
  const [statusDeleteSecondaryTherapy, setStatusDeleteSecondaryTherapy] = useState<any>(undefined)
  const [statusToggleSecondaryTherapy, setStatusToggleSecondaryTherapy] = useState<any>(undefined)
  // Status code of the last failed write, for the snackbars below: a 403 means the therapy was
  // entered by the centre (therapy-service specs/2026-10-secondary-therapy-authz.md)
  const [writeErrorStatusCode, setWriteErrorStatusCode] = useState<number | undefined>(undefined)
  const onWriteError = (_message?: string, statusCode?: number) => setWriteErrorStatusCode(statusCode)
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0)
  const [therapiesSearch, setTherapiesSearch] = useState<string>('')
  const [secondaryTherapiesSearch, setSecondaryTherapiesSearch] = useState<string>('')

  const fetchTherapies = async () => {
    const result = await apiClient.getTherapies(setStatusTherapies) ?? []
    setTherapies(result)
  }

  const fetchSecondaryTherapies = async () => {
    const result = await apiClient.getSecondaryTherapies(setStatusSecondaryTherapies) ?? []
    setSecondaryTherapies(result)
  }

  useEffect(() => {
    if (showPrimaryTherapies) {
      fetchTherapies()
    }
  }, [showPrimaryTherapies])

  useEffect(() => {
    if (showSecondaryTherapies) {
      fetchSecondaryTherapies()
    }
  }, [showSecondaryTherapies])

  const openCreateDialog = () => {
    setEditingSecondaryTherapy(undefined)
    setIsDialogOpen(true)
  }

  const openEditDialog = (secondaryTherapy: Record<string, any>) => {
    setEditingSecondaryTherapy(secondaryTherapy)
    setIsDialogOpen(true)
  }

  const onDialogSuccess = async () => {
    setIsDialogOpen(false)
    await fetchSecondaryTherapies()
  }

  const onConfirmDelete = async () => {
    if (!deletingSecondaryTherapy) { return }
    await apiClient.deleteSecondaryTherapy(deletingSecondaryTherapy._id, setStatusDeleteSecondaryTherapy, onWriteError)
    setDeletingSecondaryTherapy(undefined)
    await fetchSecondaryTherapies()
  }

  const onCloseDeleteDialog = () => {
    setDeletingSecondaryTherapy(undefined)
    setStatusDeleteSecondaryTherapy(undefined)
  }

  const onToggleStateClick = async (secondaryTherapy: Record<string, any>) => {
    // Transitional fallback to the legacy `drug.state` for documents that predate the root
    // `state` field (RF18 of therapy-service specs/2026-09-notification-secondaryTherapy.md) and
    // haven't been backfilled yet. Drop the fallback once the migration script has run everywhere.
    const currentState = secondaryTherapy.state ?? secondaryTherapy.drug?.state
    const newState = currentState === 'NOT_ACTIVE' ? 'ACTIVE' : 'NOT_ACTIVE'
    await apiClient.patchSecondaryTherapy(
      secondaryTherapy._id,
      {
        state: newState,
        // Deactivating the therapy also switches its assumption reminder off (RF18 of
        // therapy-service specs/2026-09-notification-secondaryTherapy.md) — reactivating it does
        // NOT switch the reminder back on, that stays a separate, explicit choice.
        ...(newState === 'NOT_ACTIVE' ? {reminderStatus: REMINDER_STATUSES.DISABLED} : {})
      },
      setStatusToggleSecondaryTherapy,
      onWriteError
    )
    await fetchSecondaryTherapies()
  }

  const filteredTherapies = therapies.filter((therapy) =>
    (therapy.therapyType?.name ?? '').toLowerCase().includes(therapiesSearch.toLowerCase()))

  const filteredSecondaryTherapies = secondaryTherapies.filter((secondaryTherapy) =>
    (secondaryTherapy.drug?.name ?? '').toLowerCase().includes(secondaryTherapiesSearch.toLowerCase()))

  const therapiesContent = (
    <div>
      <div style={{padding: '16px 16px 0px'}}>
        <Input.Search
          onChange={({target}) => setTherapiesSearch(target.value)}
          placeholder='Cerca...'
          style={{maxWidth: 260}}
        />
      </div>
      {statusTherapies === STATUS.PENDING && <Loader />}
      {statusTherapies !== STATUS.PENDING && filteredTherapies.length === 0 &&
        <WarningPatientEmptyData message='Nessuna terapia trovata' />}
      <div style={{display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px'}}>
        {filteredTherapies.map((therapy) => {
          const isInactive = therapy.state === 'NOT_ACTIVE'
          return (
            <CardCustom
              key={therapy._id}
              style={isInactive ? {backgroundColor: '#F0F2F5'} : undefined}
              title={
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px'}}>
                  <div style={{display: 'flex', alignItems: 'baseline', gap: '8px'}}>
                    {therapy.therapyType?.name ?? ''}
                    {therapy.state && (
                      <Chip
                        color={isInactive ? 'error' : 'success'}
                        label={formatTherapyState(therapy.state)}
                        size='small'
                      />
                    )}
                  </div>
                </div>
              }
            >
              <TherapyCardTabs therapy={therapy} />
            </CardCustom>
          )
        })}
      </div>
    </div>
  )

  const secondaryTherapiesContent = (
    <div>
      <div style={{padding: '16px 16px 0px'}}>
        <Input.Search
          onChange={({target}) => setSecondaryTherapiesSearch(target.value)}
          placeholder='Cerca...'
          style={{maxWidth: 260}}
        />
      </div>
      {statusSecondaryTherapies === STATUS.PENDING && <Loader />}
      {statusSecondaryTherapies !== STATUS.PENDING && filteredSecondaryTherapies.length === 0 &&
        <WarningPatientEmptyData message='Nessuna terapia accessoria trovata' />}
      <div style={{display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px'}}>
        {filteredSecondaryTherapies.map((secondaryTherapy) => {
          // Transitional fallback, see onToggleStateClick above.
          const state = secondaryTherapy.state ?? secondaryTherapy.drug?.state
          const isInactive = state === 'NOT_ACTIVE'
          const canUpdate = canOnSecondaryTherapy(secondaryTherapy, 'update')
          const canDelete = canOnSecondaryTherapy(secondaryTherapy, 'delete')
          return (
            <CardCustom
              key={secondaryTherapy._id}
              style={isInactive ? {backgroundColor: '#F0F2F5'} : undefined}
              title={
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px'}}>
                  <div style={{display: 'flex', alignItems: 'baseline', gap: '8px'}}>
                    {secondaryTherapy.drug?.name ?? 'Terapia accessoria'}
                    {state && (
                      <Chip
                        color={isInactive ? 'error' : 'success'}
                        label={formatTherapyState(state)}
                        size='small'
                      />
                    )}
                  </div>
                  <ReminderBell therapy={secondaryTherapy} />
                </div>
              }
            >
              <SecondaryTherapyCardTabs secondaryTherapy={secondaryTherapy} dataSchema={secondaryTherapiesDataSchema} />
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '4px', marginTop: '8px'}}>
                <Tooltip title={canUpdate ? 'Modifica' : SECONDARY_THERAPY_LOCKED_TOOLTIP}>
                  <span>
                    <IconButton color='primary' disabled={isInactive || !canUpdate} onClick={() => openEditDialog(secondaryTherapy)} size='small'>
                      <Icon path={mdiPencilOutline} size={0.8} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={canDelete ? 'Elimina' : SECONDARY_THERAPY_LOCKED_TOOLTIP}>
                  <span>
                    <IconButton color='error' disabled={isInactive || !canDelete} onClick={() => setDeletingSecondaryTherapy(secondaryTherapy)} size='small'>
                      <Icon path={mdiTrashCanOutline} size={0.8} />
                    </IconButton>
                  </span>
                </Tooltip>
                {/* Changes `state`: the same permission as the edit form */}
                <Tooltip title={canUpdate ? (isInactive ? 'Riattiva' : 'Disattiva') : SECONDARY_THERAPY_LOCKED_TOOLTIP}>
                  <span>
                    <IconButton disabled={!canUpdate} onClick={() => onToggleStateClick(secondaryTherapy)} size='small'>
                      <Icon path={isInactive ? mdiRestore : mdiClose} size={0.8} />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </CardCustom>
          )
        })}
      </div>
    </div>
  )

  const tabs = [
    {
      id: 'primaryTherapies',
      icon: <Icon path={mdiPill} size={1} />,
      label: <Typography style={{fontSize: 12, fontWeight: 600}}>{sidebarItems?.primaryTherapies?.label}</Typography>
    },
    {
      id: 'secondaryTherapies',
      icon: <Icon path={mdiPillMultiple} size={1} />,
      label: <Typography style={{fontSize: 12, fontWeight: 600}}>{sidebarItems?.secondaryTherapies?.label}</Typography>
    }
  ].filter((tab) => {
    if (tab.id === 'primaryTherapies') return showPrimaryTherapies
    if (tab.id === 'secondaryTherapies') return showSecondaryTherapies
    return false
  })

  const contents = [
    {id: 'primaryTherapies', content: therapiesContent},
    {id: 'secondaryTherapies', content: secondaryTherapiesContent}
  ].filter((content) => {
    if (content.id === 'primaryTherapies') return showPrimaryTherapies
    if (content.id === 'secondaryTherapies') return showSecondaryTherapies
    return false
  }).map(({content}) => content)

  const activeTabId = tabs[activeTabIndex]?.id
  const showAddButton = activeTabId === 'secondaryTherapies'

  return (
    <LayoutContainer
      breadcrumbs={[{label: sidebarItems?.therapies?.label, href: PATHS.THERAPIES}]}
      buttons={showAddButton ? [
        <PrimaryButton key='add-secondary-therapy' onClick={openCreateDialog}>
          <Typography style={{fontSize: 12, fontWeight: 600}}>
            {'Aggiungi'}
          </Typography>
        </PrimaryButton>
      ] : []}
    >
      <TabsCustom
        contents={contents}
        onChange={setActiveTabIndex}
        tabs={tabs}
      />
      {isDialogOpen && (
        <SecondaryTherapyDialog
          onClose={() => setIsDialogOpen(false)}
          onSuccess={onDialogSuccess}
          secondaryTherapy={editingSecondaryTherapy}
        />
      )}
      {deletingSecondaryTherapy && (
        <ConfirmationDialog
          messageId='deleteAccessoryTherapyMessage'
          onClose={onCloseDeleteDialog}
          onConfirm={onConfirmDelete}
          titleId='deleteAccessoryTherapyTitle'
        />
      )}
      <Snackbar anchorOrigin={{vertical: 'top', horizontal: 'right'}} autoHideDuration={2000} onClose={() => setStatusTherapies(undefined)} open={statusTherapies === STATUS.REJECTED}>
        <div>
          <SnackbarAlertCustom onClose={() => setStatusTherapies(undefined)} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              <FormattedMessage id='therapiesWarning' />
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
      <Snackbar anchorOrigin={{vertical: 'top', horizontal: 'right'}} autoHideDuration={2000} onClose={() => setStatusSecondaryTherapies(undefined)} open={statusSecondaryTherapies === STATUS.REJECTED}>
        <div>
          <SnackbarAlertCustom onClose={() => setStatusSecondaryTherapies(undefined)} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              <FormattedMessage id='accessoryTherapiesWarning' />
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
      <Snackbar anchorOrigin={{vertical: 'top', horizontal: 'right'}} autoHideDuration={2000} onClose={() => setStatusDeleteSecondaryTherapy(undefined)} open={statusDeleteSecondaryTherapy === STATUS.REJECTED}>
        <div>
          <SnackbarAlertCustom onClose={() => setStatusDeleteSecondaryTherapy(undefined)} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              {secondaryTherapyErrorMessage(writeErrorStatusCode, 'Errore nell\'eliminazione della terapia accessoria.')}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
      <Snackbar anchorOrigin={{vertical: 'top', horizontal: 'right'}} autoHideDuration={2000} onClose={() => setStatusToggleSecondaryTherapy(undefined)} open={statusToggleSecondaryTherapy === STATUS.REJECTED}>
        <div>
          <SnackbarAlertCustom onClose={() => setStatusToggleSecondaryTherapy(undefined)} severity='error' sx={{width: '100%'}}>
            <Typography variant='h5'>
              {secondaryTherapyErrorMessage(writeErrorStatusCode, 'Errore nell\'aggiornamento dello stato della terapia accessoria.')}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
    </LayoutContainer>
  )
}

export default Therapies
